import { Card, CardContent, Stack } from "@mui/material";
import "azure-maps-control/dist/atlas.min.css";

import Datetime from "@/common/components/Datetime/Datetime";
import BooleanValue from "@/common/components/Form/Display/BooleanValue";
import FieldValue from "@/common/components/Form/Display/FieldValue";
import AppIcon from "@/common/components/Icons/AppIcon";
import AppPopover from "@/common/components/Popover/AppPopover";
import AppPopoverContent from "@/common/components/Popover/AppPopoverContent";
import AppTypography from "@/common/components/Text/AppTypography";
import { TypeHelper } from "@/common/helpers/type";
import { GeneralProductProfileDto } from "@/core/api/generated/v1";

import AssetLink from "../../Asset/AssetLink";
import AssetSubscriptionPlanLink from "../../AssetSubscriptionPlan/AssetSubscriptionPlanLink";
import SubscriptionPlanPriceDisplay from "../../AssetSubscriptionPlan/SubscriptionPlanPriceDisplay";
import PoolLink from "../../Pool/PoolLink";
import GeneralByWhoDisplay from "../Display/GeneralByWhoDisplay";
import GeneralCountryDisplay from "../Display/GeneralCountryDisplay";

interface Props {
  value: Nil<GeneralProductProfileDto>;
}

export default function GeneralProductProfileDisplay({ value }: Props) {
  if (!value) {
    return null;
  }

  return (
    <Stack>
      <Card variant='outlined' elevation={0}>
        <CardContent>
          <AppTypography variant='h6' sx={{ mb: 1 }}>
            Base price
          </AppTypography>

          <SubscriptionPlanPriceDisplay
            currency={value.currency}
            basePrice={value.basePrice}
            price={value.basePrice}
          />
        </CardContent>
      </Card>

      <FieldValue label='Country' isEmpty={!value.country}>
        <GeneralCountryDisplay country={value.country} withDetailsPopover />
      </FieldValue>

      <FieldValue
        label='Is propagation allowed'
        direction='row'
        isEmpty={TypeHelper.isNil(value.isAllowPropagation)}
      >
        <BooleanValue value={value.isAllowPropagation} />
      </FieldValue>

      <FieldValue
        label='Is propagated'
        direction='row'
        isEmpty={TypeHelper.isNil(value.isPropagated)}
      >
        <BooleanValue value={value.isPropagated} />{" "}
        <AppPopover
          hoverBehavior={{ closeBehavior: "onTriggerOrContentMouseLeave" }}
          trigger={<AppIcon of='info' inText />}
        >
          <AppPopoverContent sx={{ minWidth: 500 }}>
            <Stack spacing={1}>
              <AppTypography variant='subtitle1'>Propagation source</AppTypography>

              <FieldValue
                label='Asset subscription plan'
                direction='row'
                isEmpty={TypeHelper.isEmpty(value.source?.assetSubscriptionPlanId)}
              >
                <AssetSubscriptionPlanLink entityId={value.source?.assetSubscriptionPlanId} />
              </FieldValue>

              <FieldValue
                label='Pool'
                direction='row'
                isEmpty={TypeHelper.isEmpty(value.source?.poolId)}
              >
                <PoolLink entity={undefined} entityId={value.source?.poolId} />
              </FieldValue>

              <FieldValue
                label='Asset'
                direction='row'
                isEmpty={TypeHelper.isEmpty(value.source?.assetId)}
              >
                <AssetLink entityId={value.source?.assetId} />
              </FieldValue>
            </Stack>
          </AppPopoverContent>
        </AppPopover>
      </FieldValue>

      <FieldValue label='Updated at' direction='row' isEmpty={TypeHelper.isEmpty(value.updatedAt)}>
        <Datetime datetime={value.updatedAt} />
      </FieldValue>

      <FieldValue label='Updated by' direction='row' isEmpty={TypeHelper.isEmpty(value.updatedBy)}>
        <GeneralByWhoDisplay who={{ userId: value.updatedBy }} />
      </FieldValue>
    </Stack>
  );
}
